import React, { useMemo } from 'react'

import { Box, Stack, Typography, Divider, Chip } from '@mui/material'
import { EvStation } from '@mui/icons-material'

import { Station } from '@/types/Station'

import { TariffAlert, TARIFF_ALERT_HINT } from '@/components/TariffAlert'
import { PopoverTooltip } from '@/components/Tooltip/PopoverTooltip'

import { haversineDistance } from '@/utils/map'
import { numericUtils } from '@/utils/numeric'
import { chargingPointStatusUtils } from '@/utils/chargingPointStatus'

const StationItem: React.FC<{
  station: Station
  userCoords: { lat: number; lng: number } | null
  onClick: (id: number) => void
}> = ({ station, userCoords, onClick }) => {
  const distance = useMemo(() => {
    if (!userCoords) return null

    return haversineDistance(userCoords, { lat: station.latitude, lng: station.longitude }) / 1000
  }, [userCoords, station.latitude, station.longitude])

  const statusChipColor = chargingPointStatusUtils.getChipColor(station.status)
  const statusLabel = chargingPointStatusUtils.getLabel(station.status)

  return (
    <Box
      key={station.id}
      sx={{
        cursor: 'pointer',
        backgroundColor: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: 4,
        overflow: 'hidden',
      }}
      onClick={() => onClick(station.id)}
    >
      <Box sx={{ position: 'relative' }}>
        {station.image ? (
          <Box
            component="img"
            src={station.image}
            alt={station.name}
            sx={{
              width: '100%',
              height: 150,
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: 160,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
            }}
          >
            <EvStation sx={{ fontSize: 44, color: '#bdbdbd' }} />
          </Box>
        )}
        {station.has_special_tariff && (
          <Box sx={{ position: 'absolute', top: 8, right: 8, maxWidth: 'calc(100% - 16px)' }}>
            <PopoverTooltip content={TARIFF_ALERT_HINT}>
              <TariffAlert opaque />
            </PopoverTooltip>
          </Box>
        )}
      </Box>
      <Stack direction="column" spacing={1} sx={{ paddingY: 1, paddingX: 2 }}>
        <Stack direction="column" spacing={0.5}>
          <Typography
            variant="h6"
            fontWeight="bold"
            fontSize={18}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'normal',
              lineHeight: 1.2,
            }}
          >
            {station.name}
          </Typography>
          <Box>
            <Chip size="small" color={statusChipColor} label={statusLabel} sx={{ fontWeight: 600 }} />
          </Box>
        </Stack>
        <Divider />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'normal',
          }}
        >
          {station.address}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {distance === null
            ? 'Distância indisponível'
            : `${numericUtils.formatNumberToBRLDecimal(distance)} km de distância`}
        </Typography>
      </Stack>
    </Box>
  )
}

export default StationItem
